import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { CharacterId, ContentLevel } from '@/types'

// ============================================================
// Asset path resolver
// ============================================================

type SpriteType = 'portrait' | 'idle' | 'celebration' | 'expression'
type Expression = string

function getAssetPath(
  characterId: CharacterId,
  type: SpriteType,
  contentLevel: ContentLevel | 5,
  variant: string = '01',
  expression?: Expression
): string {
  const base = `/characters/${characterId}`
  const l = `l${contentLevel}`

  switch (type) {
    case 'portrait':
      return `${base}/base_portraits/${characterId}_portrait_${l}_${variant}.png`
    case 'idle':
      return `${base}/idle_poses/${characterId}_idle_${l}_${variant}.png`
    case 'celebration':
      return `${base}/celebration_poses/${characterId}_celebrate_${l}_${variant}.png`
    case 'expression':
      return `${base}/expressions/${characterId}_expr_${expression}_${variant}.png`
    default:
      return `${base}/base_portraits/${characterId}_portrait_${l}_${variant}.png`
  }
}

// ============================================================
// Particle system
// ============================================================

interface Particle {
  id: number
  x: number
  y: number
  emoji: string
  size: number
  duration: number
  delay: number
}

const PARTICLE_SETS = {
  sparkle: ['✨', '⭐', '💫'],
  hearts: ['💕', '💖', '💗', '💝'],
  confetti: ['🎉', '🎊', '✨', '⭐', '💫'],
  snow: ['❄️', '❅', '✧'],
  petals: ['🌸', '🌺', '💮'],
  stars: ['⭐', '🌟', '✨'],
} as const

function ParticleOverlay({
  type,
  count = 8,
  active = true,
}: {
  type: keyof typeof PARTICLE_SETS
  count?: number
  active?: boolean
}) {
  if (!active) return null

  const emojis = PARTICLE_SETS[type]
  const particles: Particle[] = Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    emoji: emojis[i % emojis.length],
    size: 0.6 + Math.random() * 0.6,
    duration: 2 + Math.random() * 3,
    delay: Math.random() * 2,
  }))

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute text-lg"
          style={{ left: `${p.x}%`, top: `${p.y}%`, fontSize: `${p.size}rem` }}
          initial={{ opacity: 0, y: 20, scale: 0.5 }}
          animate={{
            opacity: [0, 0.8, 0],
            y: [20, -40, -80],
            scale: [0.5, 1, 0.3],
            rotate: [0, Math.random() * 180 - 90],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            repeatDelay: Math.random() * 2,
            ease: 'easeOut',
          }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  )
}

// ============================================================
// Ambient glow
// ============================================================

const CHARACTER_COLORS: Record<CharacterId, { glow: string; gradient: string }> = {
  hana: { glow: 'rgba(236, 72, 153, 0.3)', gradient: 'from-pink-500/20 via-transparent to-transparent' },
  yuki: { glow: 'rgba(56, 189, 248, 0.3)', gradient: 'from-sky-400/20 via-transparent to-transparent' },
  momo: { glow: 'rgba(251, 146, 60, 0.25)', gradient: 'from-orange-300/20 via-transparent to-transparent' },
  sumire: { glow: 'rgba(168, 85, 247, 0.3)', gradient: 'from-purple-500/20 via-transparent to-transparent' },
}

// ============================================================
// Idle Breathing Animation
// ============================================================

function IdleBreathing({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      animate={{
        scaleY: [1, 1.008, 1],
        scaleX: [1, 0.998, 1],
        y: [0, -2, 0],
      }}
      transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {children}
    </motion.div>
  )
}

// ============================================================
// Sway Animation (subtle idle movement)
// ============================================================

function IdleSway({ children, intensity = 1 }: { children: React.ReactNode; intensity?: number }) {
  return (
    <motion.div
      animate={{
        rotate: [-0.5 * intensity, 0.5 * intensity, -0.5 * intensity],
        x: [-1 * intensity, 1 * intensity, -1 * intensity],
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {children}
    </motion.div>
  )
}

// ============================================================
// Celebration Animation
// ============================================================

function CelebrationBounce({ children, active }: { children: React.ReactNode; active: boolean }) {
  if (!active) return <>{children}</>

  return (
    <motion.div
      animate={{
        y: [0, -20, 0, -10, 0],
        scaleX: [1, 0.95, 1.05, 0.98, 1],
        scaleY: [1, 1.05, 0.95, 1.02, 1],
        rotate: [0, -3, 3, -1, 0],
      }}
      transition={{
        duration: 0.8,
        ease: 'easeOut',
        repeat: 2,
        repeatDelay: 0.3,
      }}
    >
      {children}
    </motion.div>
  )
}

// ============================================================
// Expression Crossfade
// ============================================================

function ExpressionImage({
  src,
  alt,
  className,
}: {
  src: string
  alt: string
  className?: string
}) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={src}
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className={className}
      >
        {!error ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover object-top"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl bg-bg-surface/50 rounded-2xl">
            {alt === 'hana' ? '💕' : alt === 'yuki' ? '❄️' : alt === 'momo' ? '🍑' : '💜'}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

// ============================================================
// Touch/Tap Reaction
// ============================================================

function useTapReaction() {
  const [taps, setTaps] = useState<{ id: number; x: number; y: number }[]>([])
  const nextId = useRef(0)

  const addTap = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const x = clientX - rect.left
    const y = clientY - rect.top
    const id = nextId.current++

    setTaps((prev) => [...prev, { id, x, y }])
    setTimeout(() => setTaps((prev) => prev.filter((t) => t.id !== id)), 1000)
  }, [])

  return { taps, addTap }
}

function TapHearts({ taps }: { taps: { id: number; x: number; y: number }[] }) {
  const hearts = ['💖', '💕', '💗', '💝', '✨']

  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      <AnimatePresence>
        {taps.map((tap) => (
          <motion.span
            key={tap.id}
            className="absolute text-2xl"
            style={{ left: tap.x, top: tap.y }}
            initial={{ opacity: 1, scale: 0.5, y: 0 }}
            animate={{ opacity: 0, scale: 1.5, y: -60 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {hearts[Math.floor(Math.random() * hearts.length)]}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  )
}

// ============================================================
// Entrance Animation
// ============================================================

const entranceVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20,
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: { duration: 0.3 },
  },
}

// ============================================================
// Main CharacterSprite Component
// ============================================================

export type AnimationState = 'idle' | 'celebration' | 'entrance' | 'tap'

interface CharacterSpriteProps {
  characterId: CharacterId
  contentLevel: ContentLevel | 5
  expression?: Expression
  animationState?: AnimationState
  spriteType?: SpriteType
  size?: 'sm' | 'md' | 'lg' | 'full'
  showParticles?: boolean
  particleType?: keyof typeof PARTICLE_SETS
  showGlow?: boolean
  className?: string
  onTap?: () => void
}

const sizeClasses = {
  sm: 'h-36 w-28',
  md: 'h-56 w-44',
  lg: 'h-72 w-56',
  full: 'h-full w-full',
}

export function CharacterSprite({
  characterId,
  contentLevel,
  expression,
  animationState = 'idle',
  spriteType = 'portrait',
  size = 'md',
  showParticles = false,
  particleType = 'sparkle',
  showGlow = true,
  className = '',
  onTap,
}: CharacterSpriteProps) {
  const { taps, addTap } = useTapReaction()
  const colors = CHARACTER_COLORS[characterId]

  // Determine which image to show
  const type = animationState === 'celebration' ? 'celebration' : expression ? 'expression' : spriteType
  const imagePath = getAssetPath(characterId, type, contentLevel, '01', expression)

  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    addTap(e)
    onTap?.()
  }

  const isCelebrating = animationState === 'celebration'

  return (
    <motion.div
      className={`relative ${sizeClasses[size]} ${className}`}
      variants={entranceVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={handleTap}
    >
      {/* Ambient glow behind character */}
      {showGlow && (
        <motion.div
          className="absolute inset-0 rounded-full blur-3xl -z-10"
          style={{ backgroundColor: colors.glow }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [0.8, 1, 0.8],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Particle overlay */}
      <ParticleOverlay
        type={isCelebrating ? 'confetti' : particleType}
        count={isCelebrating ? 15 : 8}
        active={showParticles || isCelebrating}
      />

      {/* Tap hearts */}
      <TapHearts taps={taps} />

      {/* Character image with animations */}
      <CelebrationBounce active={isCelebrating}>
        <IdleSway intensity={isCelebrating ? 0 : 1}>
          <IdleBreathing>
            <div className={`${sizeClasses[size]} rounded-2xl overflow-hidden`}>
              <ExpressionImage
                src={imagePath}
                alt={characterId}
                className="w-full h-full"
              />
            </div>
          </IdleBreathing>
        </IdleSway>
      </CelebrationBounce>
    </motion.div>
  )
}

// ============================================================
// Convenience wrappers
// ============================================================

/** Idle character on home screen */
export function CharacterIdle(props: Omit<CharacterSpriteProps, 'animationState' | 'spriteType'>) {
  return <CharacterSprite {...props} animationState="idle" spriteType="portrait" showParticles />
}

/** Celebration after task complete / level up */
export function CharacterCelebration(props: Omit<CharacterSpriteProps, 'animationState' | 'spriteType'>) {
  return (
    <CharacterSprite
      {...props}
      animationState="celebration"
      spriteType="celebration"
      showParticles
      particleType="confetti"
    />
  )
}

/** Expression close-up for dialogue */
export function CharacterExpression({
  expression,
  ...props
}: Omit<CharacterSpriteProps, 'animationState' | 'spriteType'> & { expression: Expression }) {
  return (
    <CharacterSprite
      {...props}
      animationState="idle"
      spriteType="expression"
      expression={expression}
      showGlow={false}
    />
  )
}
